import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useAdminRouteContext from 'hooks/useAdminRouteContext';
import storage from 'shared/storage';
import { useEnsureW9Form } from '../gql';
import { readW9Params } from './routes';

// The state every W-9 page starts from (w9-plan §6.2): the account in
// ?account= (or the last one this browser picked), and that account's
// W-9 form, created on first open through ensureW9Form. `pathFor` is the
// page's own path builder (w9DashboardPath, w9LinkPath, w9SettingsPath),
// so the account lands in the URL of the page it was picked on and every
// link keeps the viewer's route prefix.

// Survives logout: shared/storage keeps every `pp_forms_` key.
export const LAST_ACCOUNT_KEY = 'pp_forms_w9_account';

export const readLastAccount = () => {
  const n = parseInt(storage.get(LAST_ACCOUNT_KEY, ''), 10);
  return n > 0 ? n : null;
};

export const rememberAccount = (id) =>
  storage.set(LAST_ACCOUNT_KEY, String(id));

const useW9Section = (pathFor) => {
  const history = useHistory();
  const location = useLocation();
  const { routePrefix } = useAdminRouteContext();
  const params = readW9Params(location.search);
  const accountId = params.account;
  const ensureW9Form = useEnsureW9Form();
  const ensureRef = useRef(ensureW9Form);
  ensureRef.current = ensureW9Form;
  const [state, setState] = useState({
    accountId: null,
    form: null,
    error: null,
    attempt: 0,
  });

  // No account in the URL: reopen the last one this browser used.
  useEffect(() => {
    if (accountId) return;
    const last = readLastAccount();
    if (last) {
      history.replace(pathFor(routePrefix, { ...params, account: last }));
    }
    // params is derived from location.search, which is in the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, location.search, history, pathFor, routePrefix]);

  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!accountId) return undefined;
    let alive = true;
    rememberAccount(accountId);
    setState({ accountId, form: null, error: null, attempt });
    ensureRef.current(accountId).then((res) => {
      if (!alive) return;
      setState({
        accountId,
        form: res && res.data ? res.data : null,
        error: res && res.error ? res.error.global : null,
        attempt,
      });
    });
    return () => {
      alive = false;
    };
  }, [accountId, attempt]);

  const pickAccount = useCallback(
    (id) => history.push(pathFor(routePrefix, { account: id })),
    [history, pathFor, routePrefix]
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const current = state.accountId === accountId;
  return {
    routePrefix,
    params,
    accountId,
    form: current ? state.form : null,
    error: current ? state.error : null,
    loading: !!accountId && (!current || (!state.form && !state.error)),
    pickAccount,
    retry,
  };
};

export default useW9Section;
